import React from 'react';

/**
 * The four ways a reel stops without a Portal to show.
 * Design handoff §4 "End states" — kicker, title, body, two buttons.
 *
 * Every one of them offers a way forward that isn't "watch the video": a clip that failed to
 * load doesn't close the Portal it belongs to, and the details are live either way.
 */
export const END_STATES = {
  failed: {
    kicker: 'PLAYBACK FAILED',
    kickerColor: '#EF4444',
    title: "THAT DIDN'T LOAD.",
    body: 'The video didn’t come through. The Portal itself is still open — you can go in without watching.',
    primary: 'TRY AGAIN',
    secondary: 'SKIP TO NEXT',
  },
  encoding: {
    kicker: 'STILL DEVELOPING…',
    kickerColor: '#EAB308',
    title: 'NOT READY YET.',
    body: (name) => `${name}’s video is still developing. It’ll be here in a minute — the details are already live.`,
    primary: 'VIEW PORTAL',
    secondary: 'SKIP TO NEXT',
  },
  loggedOut: {
    kicker: 'FRIENDS ONLY',
    kickerColor: '#00FFFF',
    title: 'THIS ONE WENT TO FRIENDS.',
    body: (name) => `${name} opened this Portal to their friends. Public Portals are still open to you.`,
    primary: 'SIGN IN',
    secondary: 'SEE PUBLIC PORTALS',
  },
  end: {
    kicker: 'LAST PORTAL',
    kickerColor: '#FFFFFF',
    title: "THAT'S EVERYONE.",
    body: (_n, count) => `${count} ${count === 1 ? 'Portal' : 'Portals'}, all caught up. Nothing new near you for now.`,
    primary: 'BACK TO FEATURED',
    secondary: 'OPEN A PORTAL',
  },
};

export default function StoryEndState({ kind, name = 'They', count = 0, onPrimary, onSecondary }) {
  const spec = END_STATES[kind];
  if (!spec) return null;
  const body = typeof spec.body === 'function' ? spec.body(name, count) : spec.body;

  return (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center px-6 text-center" style={{ background: '#000000' }}>
      <p className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.10em', color: spec.kickerColor }}>
        {spec.kicker}
      </p>
      <h2 className="mt-3 font-mono font-bold uppercase text-white" style={{ fontSize: 20, letterSpacing: '0.05em' }}>
        {spec.title}
      </h2>
      <p className="mt-3 max-w-xs" style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.70)' }}>
        {body}
      </p>

      <button
        type="button"
        onClick={onPrimary}
        className="pr-story-action mt-6 w-full max-w-xs font-mono font-semibold uppercase"
        style={{ background: '#FFFFFF', color: '#000000', border: '2px solid #FFFFFF', fontSize: 14, letterSpacing: '0.05em', padding: '14px 16px', transition: 'background-color 200ms, color 200ms' }}
      >
        {spec.primary}
      </button>
      <button
        type="button"
        onClick={onSecondary}
        className="pr-story-secondary mt-2 w-full max-w-xs font-mono font-bold uppercase text-white"
        style={{ border: '2px solid #FFFFFF', fontSize: 12, letterSpacing: '0.10em', padding: '12px 16px', transition: 'background-color 200ms, color 200ms' }}
      >
        {spec.secondary}
      </button>
    </div>
  );
}
